import { FieldValues, get, useFormContext, useWatch } from 'react-hook-form';
import { FormRadioButton } from './FormRadioButton';
import { useMemo } from 'react';
import { FormComponentProps } from '../types';

interface IRadioButtonField {
  title: string;
  value: any;
  description?: string;
}

interface FormRadioButtonGroupProps<T> extends FormComponentProps<T> {
  title: string;
  hideTitle?: boolean;
  buttons: IRadioButtonField[];
  mandatory?: boolean;
  hideErrorControl?: boolean;
}

export const FormRadioButtonGroup = <T extends FieldValues>(props: FormRadioButtonGroupProps<T>) => {
  const {
    formState: { errors },
    setValue
  } = useFormContext<T>();

  const errorForName = useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);

  const labelTextColour = () => {
    if (errorForName) {
      return 'text-feedback-red';
    }
    return 'text-solid-white';
  };

  const backgroundColour = () => {
    if (errorForName) {
      return 'bg-feedback-dark-red';
    }
    return 'bg-transparent';
  };

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };

  const fieldValue = useWatch<T>({ name: props.name });

  return (
    <div className="w-full">
      <div
        className={`${backgroundColour()} pl-3 pt-1 pb-1.5 text-solid-white placeholder-shown:text-secondary-deep-blue-30`}
      >
        <div className="flex flex-row justify-start gap-1 mb-1 pr-3">
          <label className={`cursor-text text-sm ${labelTextColour()} block`}>{props.title}</label>
          {props.mandatory && !props.readonly && (
            <label className={`text-xs ${labelTextColour()} block`}>*</label>
          )}
        </div>
        <div className="flex flex-row flex-wrap gap-4">
          {props.buttons.map((button, index) => {
            return (
              <FormRadioButton<T>
                readonly={props.readonly}
                name={props.name}
                key={index}
                title={button.title}
                value={button.value}
                description={button.description}
                group={props.title}
                setValue={setValue}
                fieldValue={fieldValue}
              />
            );
          })}
        </div>
      </div>
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
